import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { API_URL as API } from '../utils/config';

const AdminNewsletter = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !user.is_admin) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchSubscribers();
  }, [user, navigate]);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/newsletter-subscribers`, { headers });
      setSubscribers(response.data);
    } catch (error) {
      console.error('Failed to fetch subscribers:', error);
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  const copyEmails = () => {
    const emails = subscribers.map(sub => sub.email).join(', ');
    navigator.clipboard.writeText(emails);
    toast.success('E-mail addresses copied to clipboard!');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('no-NO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24 flex items-center justify-center">
        <p className="text-[#8A8A8A]">Loading subscribers...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-lg font-sans font-bold uppercase tracking-tight text-[#1A1A1A]">
            Newsletter Subscribers ({subscribers.length})
          </h1>
          <div className="flex gap-3">
            <button
              onClick={copyEmails}
              className="bg-green-600 text-white px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-green-700 transition-all"
            >
              Copy All Emails
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="bg-transparent border border-[#1A1A1A] text-[#1A1A1A] px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-[#1A1A1A] hover:text-white transition-all"
            >
              Back to Admin
            </button>
          </div>
        </div>

        {subscribers.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow-md text-center">
            <p className="text-[#8A8A8A]">No newsletter subscribers yet</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#1A1A1A] text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em]">Email</th>
                  <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em]">Subscribed Date</th>
                  <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((subscriber, index) => (
                  <tr key={subscriber.id} className={index % 2 === 0 ? 'bg-white' : 'bg-[#F9F9F7]'}>
                    <td className="px-6 py-4 text-sm text-[#1A1A1A] font-medium">
                      {subscriber.email}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#8A8A8A]">
                      {formatDate(subscriber.subscribed_at)}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(subscriber.email);
                          toast.success('Email copied!');
                        }}
                        className="text-xs uppercase tracking-[0.1em] text-[#1A1A1A] hover:text-[#C6A87C] transition-colors"
                      >
                        Copy
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-8 bg-blue-50 border border-blue-200 p-6 rounded-lg">
          <h3 className="text-sm font-bold uppercase tracking-wider text-blue-900 mb-2">
            📧 Hvordan bruke nyhetsbrev-listene
          </h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Klikk "Copy All Emails" for å kopiere alle e-postadressene</li>
            <li>• Lim inn i ditt e-postprogram (Gmail, Outlook, etc.)</li>
            <li>• Send nyhetsbrev til alle abonnenter</li>
            <li>• Nye abonnenter vises automatisk her</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminNewsletter;
